import { useCallback, useRef, useState, useEffect } from "react";
import { fileUploadService } from "@/features/settings/service/fileUploadService";
import { VITE_API_URL } from "@/core/constants/env";

export const PhotoUploadViewModel = (initialSrc: string) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [avatarUrl, setAvatarUrl] = useState<string>(initialSrc || "");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Sync when parent changes src
  useEffect(() => {
    if (initialSrc) {
      setAvatarUrl(initialSrc);
    }
  }, [initialSrc]);

  const onUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate type
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      // Validate size
      const maxSizeBytes = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSizeBytes) {
        setError("File must be smaller than 5MB");
        return;
      }

      setError(null);
      setIsUploading(true);

      // Show preview immediately
      const previewUrl = URL.createObjectURL(file);
      setAvatarUrl(previewUrl);

      try {
        // Upload file to get fid and URI
        const uploadResponse = await fileUploadService.uploadFile(file);

        // console.log("Upload response:", uploadResponse);

        // Extract FID - the response has fid as array with objects
        const fid = uploadResponse.fid?.[0]?.value?.toString();

        if (!fid) {
          throw new Error("No FID received from server");
        }

        // Extract the file URL for display
        let displayUrl: string;

        if (
          uploadResponse.uri &&
          Array.isArray(uploadResponse.uri) &&
          uploadResponse.uri[0]
        ) {
          const uriItem = uploadResponse.uri[0];
          // Use the url property if available, otherwise construct from value
          displayUrl = uriItem.url
            ? `${VITE_API_URL}${uriItem.url}`
            : `${VITE_API_URL}${uriItem.value}`;
        } else {
          throw new Error("Invalid response format from server - missing URI");
        }

        // console.log("FID:", fid);
        // console.log("Display URL:", displayUrl);

        // Clean up preview URL and set final URL for display
        URL.revokeObjectURL(previewUrl);
        setAvatarUrl(displayUrl);

        // Return the FID to the caller
        return fid;
      } catch (err) {
        console.error("Upload error:", err);

        // Revert to original avatar on error
        setAvatarUrl(initialSrc || "");

        const message =
          err instanceof Error
            ? err.message
            : "Failed to upload image. Please try again.";

        setError(message);
        throw err;
      } finally {
        setIsUploading(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [initialSrc]
  );

  return {
    fileInputRef,
    avatarUrl,
    onUploadClick,
    onFileChange,
    isUploading,
    error,
  };
};
